import React from 'react';
import { Prompt } from '../types';
import CopyButton from './CopyButton';
import HighlightedPrompt from './HighlightedPrompt';
import MarkdownContent from './MarkdownContent';
import VariableSubstitutionPanel from './VariableSubstitutionPanel';
import VariableValidationAlerts from './VariableValidationAlerts';
import { validatePromptVariables } from '../utils/variables';

interface PromptDetailProps {
  prompt: Prompt;
  onBack: () => void;
}

export const PromptDetail: React.FC<PromptDetailProps> = ({ prompt, onBack }) => {
  const variableValidation = validatePromptVariables(
    [prompt.prompts.system, prompt.prompts.user],
    prompt.variables
  );

  return (
    <div className="bg-white rounded-lg shadow-md p-6">
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-3xl font-bold text-gray-900">{prompt.metadata.title}</h1>
        <button
          onClick={onBack}
          className="px-4 py-2 text-gray-600 hover:text-gray-800 transition-colors"
        >
          ← Back to Library
        </button>
      </div>

      <div className="space-y-6">
        <div>
          <h3 className="text-lg font-semibold text-gray-900 mb-2">Description</h3>
          {prompt.metadata.description ? (
            <MarkdownContent>{prompt.metadata.description}</MarkdownContent>
          ) : (
            <p className="text-gray-700">No description provided</p>
          )}
        </div>

        <div>
          <h3 className="text-lg font-semibold text-gray-900 mb-2">Category</h3>
          <span className="inline-block bg-blue-100 text-blue-800 px-3 py-1 rounded-full text-sm">
            {prompt.metadata.category}
          </span>
        </div>

        <div>
          <h3 className="text-lg font-semibold text-gray-900 mb-2">Tags</h3>
          <div className="flex flex-wrap gap-2">
            {prompt.metadata.tags.map((tag) => (
              <span key={tag} className="bg-gray-100 text-gray-700 px-2 py-1 rounded text-sm">
                {tag}
              </span>
            ))}
          </div>
        </div>

        <VariableValidationAlerts validation={variableValidation} />

        {prompt.prompts.system && (
          <div>
            <div className="flex items-center justify-between mb-2">
              <h3 className="text-lg font-semibold text-gray-900">System Prompt</h3>
              <CopyButton text={prompt.prompts.system} label="Copy Original" />
            </div>
            <blockquote className="prompt-blockquote">
              <HighlightedPrompt
                text={prompt.prompts.system}
                variables={prompt.variables}
                markdown
              />
            </blockquote>
          </div>
        )}

        <div>
          <div className="flex items-center justify-between mb-2">
            <h3 className="text-lg font-semibold text-gray-900">User Prompt</h3>
            <CopyButton text={prompt.prompts.user} label="Copy Original" />
          </div>
          <blockquote className="prompt-blockquote">
            <HighlightedPrompt
              text={prompt.prompts.user}
              variables={prompt.variables}
              markdown
            />
          </blockquote>
        </div>

        <VariableSubstitutionPanel
          systemText={prompt.prompts.system}
          userText={prompt.prompts.user}
          variables={prompt.variables}
        />

        {prompt.variables && prompt.variables.length > 0 && (
          <div>
            <h3 className="text-lg font-semibold text-gray-900 mb-2">Variables</h3>
            <div className="overflow-x-auto">
              <table className="min-w-full bg-white border border-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">
                      Name
                    </th>
                    <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">
                      Type
                    </th>
                    <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">
                      Required
                    </th>
                    <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">
                      Description
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {prompt.variables.map((variable) => (
                    <tr key={variable.name}>
                      <td className="px-4 py-2 text-sm font-medium text-gray-900">
                        {variable.name}
                      </td>
                      <td className="px-4 py-2 text-sm text-gray-500">{variable.type}</td>
                      <td className="px-4 py-2 text-sm text-gray-500">
                        {variable.required ? 'Yes' : 'No'}
                      </td>
                      <td className="px-4 py-2 text-sm text-gray-500">
                        {variable.description || '-'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default PromptDetail;
